import { ModalProgrammatic } from 'buefy';
import BaseDialog from '@/components/base/BaseDialog.vue';
import Vue from 'vue';
import store from '@/store/store';

/**
 * Displays a confirmation dialog using BaseDialog.vue
 *
 * @returns {Promise<boolean>} A prokmise that resolves to true if the user confirms, false otherwise
 */

export const showUnsavedChangesDialog = async (): Promise<boolean> => {
  // Dispatch action to set that the unsaved changes dialog is now active.
  // This should be done before the modal is opened.
  store.dispatch('unsavedChangesGuard/setUnsavedChangesDialogActive', true);

  return new Promise<boolean>((resolvePromise) => {
    let eventHandled = false;

    // Renamed the inner resolve to avoid confusion if store was passed as a prop/arg.
    const resolve = (value: boolean) => {
      if (eventHandled) return;
      eventHandled = true;
      resolvePromise(value);
    };

    try {
      ModalProgrammatic.open({
        parent: new Vue({ store }),
        // Store the instance
        component: BaseDialog,
        hasModalCard: true,
        props: {
          title: 'Discard Unsaved Changes?',
          content:
            'You have unsaved changes. If you continue, your edits will be lost.',
          confirmButtonText: 'Discard Changes',
          cancelButtonText: 'Keep Editing',
          confirmButton: true,
          cancelButton: true,
          showCloseButton: true,
          isUnsavedChangesConfirmationDialog: true,
        },
        events: {
          confirm: () => {
            resolve(true);
          },
          cancel: () => {
            resolve(false);
          },
        },
        canCancel: false, // This means clicking outside or pressing ESC won't close it by default Buefy behavior
        onCancel: () => {
          // This callback is for when Buefy's modal would close via ESC or overlay click if canCancel was true
          resolve(false);
        },
      });
    } catch (error) {
      // If an error occurs here, the promise might never resolve,
      // so we should resolve false to prevent hanging.
      if (!eventHandled) {
        // eventHandled = true; // resolve already handles this
        resolve(false);
      }
    }
  }).finally(() => {
    // Dispatch action to set that the unsaved changes dialog is no longer active.
    // This will run whether the promise resolves or rejects (if it could reject).
    store.dispatch('unsavedChangesGuard/setUnsavedChangesDialogActive', false);
  });
};
